import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Archive, Loader2, Pencil, Plus, Search, Trash2 } from "lucide-react";
import axiosInstance from "../utils/axiosInstance";
import PaginationControls from "./PaginationControls";

const StorageList = () => {
  const navigate = useNavigate();
  const [storages, setStorages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchText, setSearchText] = useState("");
  const [query, setQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [count, setCount] = useState(0);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);

  const totalPages = Math.max(1, Math.ceil((count || 0) / pageSize));

  useEffect(() => {
    const handler = setTimeout(() => {
      setQuery(searchText.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchText]);

  const fetchStorages = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        page: currentPage,
        page_size: pageSize,
        ...(query && { search: query }),
      };
      const { data } = await axiosInstance.get("/storages/", { params });
      setStorages(Array.isArray(data.results) ? data.results : []);
      setCount(data.count || 0);
      setNext(data.next || null);
      setPrevious(data.previous || null);
    } catch (err) {
      setError("خطا در دریافت لیست انبارها");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStorages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, query]);

  const handleDelete = async (id) => {
    if (!window.confirm("آیا مطمئن هستید؟")) return;
    await axiosInstance.delete(`/storages/${id}/`);
    fetchStorages();
  };

  const pageStart = useMemo(
    () => (currentPage - 1) * pageSize,
    [currentPage, pageSize]
  );

  return (
    <div className="mt-24 px-3 sm:px-6 font-vazirmatn" dir="rtl">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white/95 backdrop-blur rounded-2xl shadow-lg border border-slate-200/80 overflow-hidden">
          <div className="px-5 sm:px-7 py-5 border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
            <div className="flex flex-col gap-3 sm:flex-row-reverse sm:items-center sm:justify-between">
              <Link
                to="/storages/new"
                className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-white bg-primary hover:opacity-95 shadow-sm"
              >
                <Plus size={18} />
                انبار جدید
              </Link>

              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                  <Archive size={22} />
                </div>
                <div className="text-right">
                  <h2 className="text-lg sm:text-xl font-extrabold text-slate-800">
                    لیست انبارها
                  </h2>
                  <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                    تعریف و مدیریت محل نگهداری کالاها
                    {" "}• مجموع: <span className="font-semibold text-slate-700">{count}</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-700 text-right mb-1.5">
                جستجو
              </label>
              <div className="relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="نام، آدرس یا تلفن انبار..."
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-10 text-right text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary"
                />
              </div>
            </div>
          </div>

          <div className="px-5 sm:px-7 py-4 border-b border-slate-200 bg-white">
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              pageSizeOptions={[10, 20, 50, 100]}
              hasNext={!!next}
              hasPrevious={!!previous}
              onPageChange={(e) => setCurrentPage(Number(e.target.value))}
              onNextPage={() => next && setCurrentPage((p) => p + 1)}
              onPreviousPage={() => previous && setCurrentPage((p) => p - 1)}
              onPageSizeChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="px-5 sm:px-7 py-6">
            {loading ? (
              <div className="flex items-center justify-center gap-2 text-slate-600">
                <Loader2 className="animate-spin" size={18} />
                در حال بارگذاری...
              </div>
            ) : error ? (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-right text-rose-700 text-sm">
                {error}
              </div>
            ) : storages.length === 0 ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-right text-slate-700 text-sm">
                هیچ انباری تعریف نشده است.
              </div>
            ) : (
              <div className="w-full overflow-auto rounded-2xl border border-slate-200">
                <table className="w-full min-w-[900px] text-sm bg-white">
                  <thead className="bg-slate-50 text-slate-700">
                    <tr className="border-b border-slate-200">
                      <th className="px-3 py-3 text-right font-semibold">ردیف</th>
                      <th className="px-3 py-3 text-right font-semibold">نام انبار</th>
                      <th className="px-3 py-3 text-right font-semibold">آدرس</th>
                      <th className="px-3 py-3 text-right font-semibold">تلفن</th>
                      <th className="px-3 py-3 text-right font-semibold">تعداد کالا</th>
                      <th className="px-3 py-3 text-right font-semibold">عملیات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {storages.map((storage, index) => (
                      <tr key={storage.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                        <td className="px-3 py-3 text-slate-700">{pageStart + index + 1}</td>
                        <td className="px-3 py-3 font-semibold">
                          <Link to={`/storages/${storage.id}`} className="text-blue-700 hover:underline">
                            {storage.storage_name}
                          </Link>
                        </td>
                        <td className="px-3 py-3 text-slate-700">{storage.storage_address || "-"}</td>
                        <td className="px-3 py-3 text-slate-700">{storage.storage_phone || "-"}</td>
                        <td className="px-3 py-3 text-slate-700">{storage.goods_count || 0}</td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => navigate(`/storages/${storage.id}/edit`)}
                              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50"
                            >
                              <Pencil size={14} />
                              ویرایش
                            </button>
                            <button
                              onClick={() => handleDelete(storage.id)}
                              className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs font-bold text-rose-700 hover:bg-rose-100"
                            >
                              <Trash2 size={14} />
                              حذف
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StorageList;
